import { useEffect, useState } from 'react'
import { useProjectPlanSnapshotRepository } from '../../../../../../../services/adaptors/projectPlanSnapshotRepositoryAdaptor'
import {
  fetchProjectPlanSelectRows,
  fetchTicketListSelectRows,
} from '../../../../lib/snapshotRows'
import { buildAncestorPathText } from '../../../../lib/ancestorPathText'
import { ProjectPlanSelectRow } from '../../ProjectPlanSelect/model'
import { TicketListSelectRow } from '../model'

type UseTicketListSelectDataProps = {
  snapshotUuid: string
}

const toPathTextByUuid = (
  ticketListRows: TicketListSelectRow[],
  projectPlanRows: ProjectPlanSelectRow[]
): ReadonlyMap<string, string> => {
  const nameByUuid = new Map(
    projectPlanRows.map(row => [row.uuid, row.displayName])
  )
  return new Map(
    ticketListRows.map(row => [
      row.uuid,
      buildAncestorPathText(row.path, nameByUuid),
    ])
  )
}

export const useTicketListSelectData = ({
  snapshotUuid,
}: UseTicketListSelectDataProps) => {
  const [rows, setRows] = useState<TicketListSelectRow[]>([])
  const [pathTextByUuid, setPathTextByUuid] = useState<
    ReadonlyMap<string, string>
  >(new Map())
  const [isLoading, setIsLoading] = useState(true)

  const { fetchMasterSchedule, fetchMasterScheduleTicketLists } =
    useProjectPlanSnapshotRepository()

  useEffect(() => {
    const cancelled = { current: false }
    setIsLoading(true)
    // パスが後から出て行の高さがずれないよう、両方そろってから表示する
    Promise.allSettled([
      fetchTicketListSelectRows(snapshotUuid, fetchMasterScheduleTicketLists),
      fetchProjectPlanSelectRows(snapshotUuid, fetchMasterSchedule),
    ])
      .then(([ticketListResult, projectPlanResult]) => {
        if (cancelled.current) return
        // 失敗時のユーザー通知は共通API層(lib/commons/api)が行うため、ここでは状態のみ戻す
        if (ticketListResult.status === 'rejected') {
          console.error(
            'Failed to fetch ticket list data:',
            ticketListResult.reason
          )
          setRows([])
          setPathTextByUuid(new Map())
          return
        }
        if (projectPlanResult.status === 'rejected') {
          console.error(
            'Failed to fetch project plan data:',
            projectPlanResult.reason
          )
        }
        setRows(ticketListResult.value)
        setPathTextByUuid(
          toPathTextByUuid(
            ticketListResult.value,
            projectPlanResult.status === 'fulfilled'
              ? projectPlanResult.value
              : []
          )
        )
      })
      .finally(() => {
        if (!cancelled.current) setIsLoading(false)
      })
    return () => {
      cancelled.current = true
    }
  }, [snapshotUuid, fetchMasterSchedule, fetchMasterScheduleTicketLists])

  return { rows, pathTextByUuid, isLoading }
}
